import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { login as apiLogin, getMe } from '../api';
import authManager from '../utils/authManager';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const logoutCallbackRef = useRef(null);

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('consulterest_token');
      if (token) {
        try {
          const response = await getMe();
          setUser(response.data);
        } catch (error) {
          // Si getMe falla con 401, el interceptor ya manejará el logout
          // Solo limpiamos el estado local aquí
          if (error.response?.status !== 401) {
            localStorage.removeItem('consulterest_token');
            localStorage.removeItem('consulterest_user');
            setUser(null);
          }
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, []);

  // Registrar callback de logout en AuthManager cuando el componente se monta
  useEffect(() => {
    // Función que limpia el estado del usuario en React
    const handleLogout = () => {
      setUser(null);
    };

    logoutCallbackRef.current = handleLogout;
    authManager.registerLogoutCallback(handleLogout);

    // Cleanup: desregistrar callback cuando el componente se desmonte
    return () => {
      if (logoutCallbackRef.current) {
        authManager.unregisterLogoutCallback(logoutCallbackRef.current);
      }
    };
  }, []);

  const login = async (identifier, password, type = 'email') => {
    const response = await apiLogin(identifier, password, type);
    const { access_token, user: userData } = response.data;
    localStorage.setItem('consulterest_token', access_token);
    localStorage.setItem('consulterest_user', JSON.stringify(userData));
    setUser(userData);
    return userData;
  };

  const logout = () => {
    // Usar AuthManager para logout completo (incluye limpieza y redirección)
    authManager.logout({
      redirect: true,
      redirectTo: '/login',
    });
    // El setUser se hará automáticamente mediante el callback registrado
  };

  const isAdmin = user?.role === 'ADMIN';
  const isPatient = user?.role === 'PACIENTE';
  const isAssistant = user?.role === 'ASISTENTE';

  return (
    <AuthContext.Provider value={{ user, login, logout, loading, isAdmin, isPatient, isAssistant }}>
      {children}
    </AuthContext.Provider>
  );
};
